import React, { forwardRef, useId } from "react";
import { visuallyHiddenStyle } from "../../../theme";
import { styled } from "../../../theme/styled/styled";
import { CheckmarkIcon } from "../../icons/new-icons";
import { Title } from "../../typography/Typography";
import { Flex } from "../../util/Flex";
import { InputCard } from "../shared/CardInput";
import { InputList } from "../shared/List";
import { CheckboxGroup } from "./CheckboxGroup";

export interface CheckboxProps {
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  checked: boolean;
  label: string;
  hideLabel?: boolean;
  dataTestId?: string;
}

const CheckboxComponent = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      /** Text that places next to the checkbox input */
      label,
      /** Sets `disabled` attribute and styling on the checkbox element */
      disabled,
      /** Sets `checked` attribute and styling for the checkbox element */
      checked,
      /** Unique test id for component */
      dataTestId,
      /** Used to toggle checked state and what else needed */
      onChange,
      hideLabel = false,
    }: CheckboxProps,
    ref,
  ): React.JSX.Element => {
    const labelId = useId();

    return (
      <Label
        data-test-id={dataTestId}
        dataComponentType="Checkbox"
        component="label"
        isDisabled={disabled}
      >
        <HiddenInput
          type="checkbox"
          aria-checked={checked}
          disabled={disabled}
          checked={checked}
          onChange={() => !disabled && onChange?.(!checked)}
          aria-labelledby={labelId}
          ref={ref}
        />

        <VisualInput
          aria-hidden
          disabled={disabled}
          checked={checked}
          className="visualInput"
        >
          {checked && (
            <CheckmarkIcon
              size={16}
              colorToken={disabled ? "$on-surface-disabled" : "$text-inverted"}
            />
          )}
        </VisualInput>

        <LabelText
          color={disabled ? "$text-secondary" : "$text-primary"}
          size="medium"
          hidden={hideLabel}
          id={labelId}
          as="span"
        >
          {label}
        </LabelText>
      </Label>
    );
  },
);

const LabelText = styled(Title)<{ hidden: boolean }>(
  {
    paddingLeft: "$spacing-16",
    fontWeight: "400",
  },
  ({ hidden }) => {
    if (hidden) {
      return visuallyHiddenStyle;
    }
    return {};
  },
);

const HiddenInput = styled.input({
  ...visuallyHiddenStyle,

  ".keyboardFocus &:focus + .visualInput": {
    outline: "2px solid $focus-border",
  },
});

const VisualInput = styled.div<{ checked: boolean; disabled?: boolean }>(
  (p) => ({
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    justifyContent: "center",
    width: "20px",
    height: "20px",
    cursor: p.disabled ? "default" : "pointer",
    boxSizing: "border-box",
    background: p.disabled
      ? "$surface-disabled"
      : p.checked
      ? "$active-highlight"
      : "transparent",
    borderRadius: "$radius-xsmall",
    border: p.disabled
      ? "1px solid $surface-disabled"
      : p.checked
      ? "1px solid $active-highlight"
      : "1px solid $border-input",
  }),
);

const Label = styled(Flex)<{ isDisabled?: boolean }>((p) => ({
  cursor: p.isDisabled ? "default" : "pointer",
}));

export const Checkbox = Object.assign(CheckboxComponent, {
  Group: CheckboxGroup,
  Card: InputCard,
  List: InputList,
});
